/* seoText — small helpers for generating clean, human-friendly meta
 * descriptions from recipe fields.
 *
 * Recipe body content lives in `parsed_text` as raw markdown (headings,
 * lists, bold, links) — great for reading but ugly when Google/Bing render
 * it into a search snippet ("# Basic Suet Dumplings\n\n- 6 oz..."). This
 * strips the markup and gives us a snappy, ≤200-char summary.
 */

/** Strip common markdown / control chars from a plain-ish string. */
export function stripMarkdown(input) {
  if (!input || typeof input !== "string") return "";
  return input
    /* Headings: leading `# `, `## ` etc. */
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    /* Bullet / numbered list markers at line start. */
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    /* Bold / italic wrappers (**foo**, *foo*, __foo__, _foo_). */
    .replace(/(\*\*|__)(.*?)\1/g, "$2")
    .replace(/(\*|_)(.*?)\1/g, "$2")
    /* Inline code + fenced code fences. */
    .replace(/```[\s\S]*?```/g, "")
    .replace(/`([^`]+)`/g, "$1")
    /* Links [text](url) → text; images ![alt](url) dropped. */
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    /* Blockquotes and horizontal rules. */
    .replace(/^\s{0,3}>\s?/gm, "")
    .replace(/^\s*[-*_]{3,}\s*$/gm, "")
    /* HTML entities that survive Mongo round-trip. */
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&#x?[0-9a-f]+;/gi, " ")
    /* Collapse newlines + repeated whitespace. */
    .replace(/[\r\n]+/g, ". ")
    .replace(/\.\s*\./g, ".")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/** Trim to `max` chars on a word boundary, appending an ellipsis if cut. */
export function truncate(input, max = 200) {
  const s = (input || "").trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const boundary = cut.lastIndexOf(" ");
  return (boundary > max * 0.6 ? cut.slice(0, boundary) : cut).replace(/[,;:.\s]+$/, "") + "…";
}

/**
 * Build a human-friendly meta description for a recipe.
 * Priority chain:
 *   1. `recipe.seo_description` (admin override)
 *   2. Cleaned & truncated `recipe.parsed_text`
 *   3. Ingredients preview
 *   4. Generic fallback tied to the title (still unique per page).
 */
export function buildRecipeMetaDescription(recipe) {
  if (!recipe) return "";
  if (recipe.seo_description) return truncate(recipe.seo_description, 200);
  const cleaned = truncate(stripMarkdown(recipe.parsed_text || ""), 200);
  if (cleaned && cleaned.length > 40) return cleaned;
  const ings = (recipe.ingredients || []).map((i) => (typeof i === "string" ? i : i?.name || "")).filter(Boolean);
  if (ings.length) {
    return truncate(`${recipe.title || "Recipe"} — made with ${ings.slice(0, 6).join(", ")}. Save, share and cook on Recipe Filer.`, 200);
  }
  return truncate(`${recipe.title || "A recipe"} — saved, shared and printable on Recipe Filer.`, 200);
}

/** Same idea, but for historical recipes (which have a proper `description`
 * field already — mostly this normalises it and applies the seo override). */
export function buildHistoricalMetaDescription(recipe) {
  if (!recipe) return "";
  if (recipe.seo_description) return truncate(recipe.seo_description, 200);
  const desc = stripMarkdown(recipe.description || "");
  if (desc) return truncate(desc, 200);
  return truncate(
    `${recipe.title || "A historical recipe"} from ${recipe.source_book || "a public-domain cookbook"}${recipe.author ? " by " + recipe.author : ""}${recipe.year ? " (" + recipe.year + ")" : ""} — modernised for a modern kitchen.`,
    200,
  );
}
